<template>
  <div class="auth">
    <div class="auth__scene" aria-hidden="true">
      <div class="auth__scene-fill"></div>
      <div class="auth__scene-photo"></div>
      <div class="auth__scene-wash"></div>
      <div class="auth__scene-rose"></div>
    </div>
    <!-- Left visual -->
    <div class="auth__hero">
      <div class="auth__hero-overlay"></div>
      <div class="auth__hero-content">
        <div class="brand">
          <svg class="brand__icon" viewBox="0 0 40 40" fill="none">
            <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="#fff"/>
          </svg>
          <span class="brand__name">KissMora</span>
        </div>
        <div class="hero-poster">
          <img src="/brand/poster.png" alt="KissMora — Find your spark"/>
        </div>
        <div class="hero-quote">
          <h2>Welcome back,<br/>beautiful.</h2>
          <p>Find your spark. Your next story is one login away.</p>
        </div>
        <div class="hero-stats">
          <div><strong>250K+</strong><span>Singles</span></div>
          <div><strong>4.9★</strong><span>Rated</span></div>
          <div><strong>120+</strong><span>Countries</span></div>
        </div>
      </div>
    </div>

    <!-- Right form -->
    <div class="auth__form-wrap">
      <div class="auth__form">
        <div class="auth__top">
          <div class="brand brand--mobile">
            <svg class="brand__icon" viewBox="0 0 40 40" fill="none">
              <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="url(#lkg)"/>
              <defs>
                <linearGradient id="lkg" x1="8" y1="8" x2="32" y2="34"><stop stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
              </defs>
            </svg>
            <span class="brand__name">KissMora</span>
          </div>
          <button class="backbtn" @click="goHome" aria-label="Home">←</button>
        </div>

        <h1 class="auth__title">Sign in to your heart.</h1>
        <p class="auth__sub">Enter your details to continue your journey.</p>

        <form class="form" @submit.prevent="doLogin">
          <label class="field">
            <span class="field__label">Username or Email</span>
            <div class="field__box">
              <span class="field__ico">👤</span>
              <input v-model="username" type="text" autocomplete="username" placeholder="your.username"/>
            </div>
          </label>

          <label class="field">
            <span class="field__label">Password</span>
            <div class="field__box">
              <span class="field__ico">🔒</span>
              <input v-model="password" :type="showPass?'text':'password'" autocomplete="current-password" placeholder="••••••••"/>
              <button type="button" class="eye" @click="showPass=!showPass" aria-label="Toggle password">
                {{ showPass ? '🙈' : '👁️' }}
              </button>
            </div>
          </label>

          <div class="row">
            <label class="check">
              <input type="checkbox" v-model="remember"/>
              <span>Remember me</span>
            </label>
            <a class="link" href="#" @click.prevent="forgot">Forgot password?</a>
          </div>

          <button type="submit" class="btn btn--primary" :disabled="loading">
            <span v-if="!loading">Sign in</span>
            <span v-else class="spinner"></span>
          </button>

          <template v-if="oauth.google || oauth.facebook || oauth.apple">
            <div class="divider"><span>or continue with</span></div>
            <div class="socials">
              <button v-if="oauth.google" type="button" class="social" :disabled="oauthBusy" @click="signInGoogle">
                <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC04" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.47 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                Google
              </button>
              <button v-if="oauth.facebook" type="button" class="social" :disabled="oauthBusy" @click="signInFacebook">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877F2"><path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.93-1.95 1.88V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z"/></svg>
                Facebook
              </button>
              <button v-if="oauth.apple" type="button" class="social" :disabled="oauthBusy" @click="signInApple">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#000"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39A4.9 4.9 0 0 1 8.82 6.88c1.29-.02 2.5.87 3.3.87.8 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.39 2.83ZM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11Z"/></svg>
                Apple
              </button>
            </div>
          </template>
        </form>

        <p class="auth__foot">
          New to KissMora? <router-link to="/Register">Create an account</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
function loadScript(id, src){
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.id = id; s.src = src; s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

export default {
  name: 'AuthLogin',
  data(){
    return {
      username:'', password:'',
      showPass:false, remember:false,
      loading:false,
      oauthBusy: false,
      oauth: { google:null, facebook:null, apple:null }
    }
  },
  created(){
    if(localStorage.getItem('token')) this.$router.replace('/Home');
    const saved = localStorage.getItem('remember_username');
    if(saved){ this.username = saved; this.remember = true; }
    this.loadOAuthConfig();
  },
  methods:{
    goHome(){ this.$router.push('/'); },
    forgot(){ this.$toast('Password reset coming soon — contact support for now.'); },
    soon(){ this.$toast('Social login coming soon!'); },

    // ===== OAuth plumbing =====
    loadOAuthConfig(){
      this.$http({ method:'get', url:'oauth_config' }).then(res => {
        if (res && res.code === 200 && res.data) {
          this.oauth = res.data;
          // Pre-load the provider SDKs we actually need
          if (this.oauth.google)   loadScript('g-sdk', 'https://accounts.google.com/gsi/client').catch(()=>{});
          if (this.oauth.facebook) loadScript('fb-sdk', 'https://connect.facebook.net/en_US/sdk.js').catch(()=>{});
          if (this.oauth.apple)    loadScript('a-sdk', 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js').catch(()=>{});
        }
      }).catch(()=>{});
    },
    finishOAuth(provider, token, hintName){
      this.oauthBusy = true;
      this.$http({
        method:'post', url:'oauth_login',
        data: { provider, token, name: hintName || '' }
      }).then(res => {
        this.oauthBusy = false;
        if (res && res.code === 200 && res.data && res.data.id) {
          localStorage.setItem('token', res.data.id);
          if (res.data.header_img) { try { localStorage.setItem('km_avatar', res.data.header_img); } catch(e) { /* storage may be disabled */ } }
          this.$toast.success ? this.$toast.success(res.msg || 'Signed in') : this.$toast(res.msg);
          setTimeout(()=>this.$router.replace('/Home'), 400);
        } else {
          this.$toast.fail ? this.$toast.fail((res && res.msg) || 'Sign-in failed') : this.$toast((res && res.msg) || 'Sign-in failed');
        }
      }).catch(()=> { this.oauthBusy = false; this.$toast.fail ? this.$toast.fail('Network error') : this.$toast('Network error'); });
    },

    // ---- Google ----
    signInGoogle(){
      if (!this.oauth.google || !window.google) {
        this.$toast('Google sign-in is still loading — try again in a second'); return;
      }
      const self = this;
      try {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: this.oauth.google.client_id,
          scope: 'openid email profile',
          callback: () => { /* we use the id_token flow below */ }
        });
        // Prefer the one-tap / id_token flow (cleanest)
        window.google.accounts.id.initialize({
          client_id: this.oauth.google.client_id,
          callback: (response) => {
            if (response && response.credential) {
              self.finishOAuth('google', response.credential);
            } else {
              self.$toast.fail ? self.$toast.fail('Google sign-in was cancelled') : self.$toast('Cancelled');
            }
          }
        });
        window.google.accounts.id.prompt();
        // Keep the OAuth2 client around in case prompt is blocked
        window.__kmGoogleClient = client;
      } catch(e){
        this.$toast.fail ? this.$toast.fail('Google SDK not ready') : this.$toast('Google SDK not ready');
      }
    },

    // ---- Facebook ----
    signInFacebook(){
      if (!this.oauth.facebook || !window.FB) {
        this.$toast('Facebook sign-in is still loading — try again in a second'); return;
      }
      if (!window.__kmFbInited) {
        window.FB.init({ appId: this.oauth.facebook.app_id, cookie: false, xfbml: false, version: 'v18.0' });
        window.__kmFbInited = true;
      }
      const self = this;
      window.FB.login(function(response){
        if (response && response.authResponse && response.authResponse.accessToken) {
          self.finishOAuth('facebook', response.authResponse.accessToken);
        } else {
          self.$toast.fail ? self.$toast.fail('Facebook sign-in was cancelled') : self.$toast('Cancelled');
        }
      }, { scope: 'email,public_profile' });
    },

    // ---- Apple ----
    signInApple(){
      if (!this.oauth.apple || !window.AppleID) {
        this.$toast('Apple sign-in is still loading — try again in a second'); return;
      }
      const self = this;
      try {
        window.AppleID.auth.init({
          clientId: this.oauth.apple.service_id,
          scope: 'name email',
          redirectURI: this.oauth.apple.redirect_uri || (window.location.origin + '/Login'),
          usePopup: true
        });
        window.AppleID.auth.signIn().then(function(data){
          const token = data && data.authorization && data.authorization.id_token;
          const name  = data && data.user && data.user.name ? (data.user.name.firstName + ' ' + (data.user.name.lastName || '')).trim() : '';
          if (token) self.finishOAuth('apple', token, name);
          else self.$toast.fail ? self.$toast.fail('Apple did not return a token') : self.$toast('No token');
        }).catch(function(){
          self.$toast('Apple sign-in was cancelled');
        });
      } catch(e){
        this.$toast.fail ? this.$toast.fail('Apple SDK not ready') : this.$toast('Apple SDK not ready');
      }
    },
    doLogin(){
      if(!this.username) return this.$toast('Please enter your username');
      if(!this.password) return this.$toast('Please enter your password');
      this.loading = true;
      this.$http({
        url:'member_login', method:'get',
        data:{ username:this.username, password:this.password }
      }).then(res=>{
        this.loading = false;
        if(res && res.code === 200){
          this.$toast.success('Welcome back!');
          localStorage.setItem('token', res.data.id);
          if(this.remember) localStorage.setItem('remember_username', this.username);
          else localStorage.removeItem('remember_username');
          const must = res.data && Number(res.data.must_change_password) === 1;
          if (must) {
            localStorage.setItem('must_change_password', '1');
            this.$toast('Please set a new password to continue');
            this.$router.replace({ name: 'SetLoginPassword', query: { force: '1' } });
          } else {
            localStorage.removeItem('must_change_password');
            this.$router.push('/Home');
          }
        } else {
          this.$toast((res && res.msg) || 'Login failed');
        }
      }).catch(()=>{ this.loading = false; this.$toast('Network error, try again'); });
    }
  }
}
</script>

<style scoped>
.auth {
  position: relative;
  min-height: 100vh;
  display: grid; grid-template-columns: 1.05fr 0.95fr;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  background: #7ec8e8;
  overflow: hidden;
}
.auth * { box-sizing: border-box; }

/* Full portrait behind both columns. Rose panel crossfades into it. */
.auth__scene { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
.auth__scene-fill,
.auth__scene-photo,
.auth__scene-wash,
.auth__scene-rose { position: absolute; inset: 0; }
.auth__scene-fill {
  inset: -48px;
  background: url('/img/login-portrait.png') center / cover no-repeat;
  filter: blur(42px) saturate(1.25);
  transform: scale(1.12);
}
.auth__scene-photo {
  top: -6%;
  bottom: auto;
  left: 36%;
  right: auto;
  width: auto;
  height: 118%;
  aspect-ratio: 659 / 1000;
  background: url('/img/login-portrait.png') center / cover no-repeat;
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 16%, #000 94%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0%, #000 16%, #000 94%, transparent 100%);
}
.auth__scene-wash {
  background: linear-gradient(100deg,
    rgba(255, 49, 132, .2) 0%,
    rgba(196, 40, 176, .34) 22%,
    rgba(150, 40, 200, .14) 42%,
    rgba(140, 50, 200, 0) 62%);
}
.auth__scene-rose {
  background:
    linear-gradient(145deg, rgba(255,77,141,.82) 0%, rgba(160,32,240,.88) 100%),
    url('/brand/poster.png') left center / cover no-repeat;
  -webkit-mask-image: linear-gradient(90deg, #000 0%, #000 24%, transparent 48%);
  mask-image: linear-gradient(90deg, #000 0%, #000 24%, transparent 48%);
}

/* HERO */
.auth__hero {
  position: relative; z-index: 1;
  background: none;
  min-height: 100vh;
}
.auth__hero-overlay { display: none; }
.auth__hero-content {
  position: relative; z-index: 1; height: 100%;
  padding: 48px; color: #fff;
  display: flex; flex-direction: column; justify-content: space-between; min-height: 100vh;
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand__icon { width: 36px; height: 36px; }
.brand__name { font-size: 24px; font-weight: 800; letter-spacing: -.02em; }
.hero-poster {
  width: min(100%, 340px); aspect-ratio: 1; border-radius: 22px; overflow: hidden;
  box-shadow: 0 24px 50px rgba(0,0,0,.4); margin: 8px 0 12px;
  background: #000;
}
.hero-poster img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hero-quote h2 { font-size: 52px; line-height: 1.05; font-weight: 800; margin: 0 0 16px; letter-spacing: -.02em; }
.hero-quote p { font-size: 18px; opacity: .9; max-width: 420px; }
.hero-stats { display: flex; gap: 40px; }
.hero-stats strong { display: block; font-size: 28px; font-weight: 800; margin-bottom: 4px; }
.hero-stats span { font-size: 13px; opacity: .8; letter-spacing: .05em; text-transform: uppercase; }

/* FORM — glass card floats on the portrait */
.auth__form-wrap {
  position: relative; z-index: 1;
  display: flex; align-items: center; justify-content: center;
  margin: 0;
  padding: 36px 28px;
  background: none;
}
.auth__form-wrap::before,
.auth__form-wrap::after { content: none; }
.auth__form {
  position: relative; z-index: 1;
  width: 100%; max-width: 420px;
  padding: 28px 28px 22px;
  background: rgba(255,255,255,.34);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid rgba(255,255,255,.62);
  border-radius: 28px;
  box-shadow:
    0 28px 60px rgba(16, 24, 40, .22),
    inset 0 1px 0 rgba(255,255,255,.78);
}
.auth__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; }
.brand--mobile { display: none; }
.backbtn {
  background: rgba(26,16,51,.06); border: none; width: 40px; height: 40px; border-radius: 50%;
  font-size: 20px; cursor: pointer; color: #1a1033; transition: background .2s;
}
.backbtn:hover { background: rgba(26,16,51,.12); }
.auth__title {
  font-size: 34px; font-weight: 800; margin: 0 0 8px; color: #1a1033; letter-spacing: -.02em;
}
.auth__sub { color: #6b5c85; font-size: 15px; margin: 0 0 32px; }

.form { display: flex; flex-direction: column; gap: 18px; }
.field { display: flex; flex-direction: column; gap: 8px; }
.field__label { font-size: 13px; font-weight: 600; color: #1a1033; }
.field__box {
  display: flex; align-items: center; background: #fff; border: 2px solid #eee2ec;
  border-radius: 14px; padding: 0 14px; transition: border-color .2s, box-shadow .2s;
}
.field__box:focus-within {
  border-color: #ff4d8d; box-shadow: 0 0 0 4px rgba(255,77,141,.1);
}
.field__ico { font-size: 18px; margin-right: 10px; }
.field__box input {
  flex: 1; border: none; outline: none; background: transparent;
  padding: 14px 0; font-size: 15px; color: #1a1033; font-family: inherit;
}
.field__box input::placeholder { color: #b8a9c9; }
.eye { background: none; border: none; cursor: pointer; font-size: 18px; padding: 4px 0 4px 8px; }

.row { display: flex; justify-content: space-between; align-items: center; margin: 4px 0 8px; }
.check { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #4a3d66; cursor: pointer; }
.check input { accent-color: #ff4d8d; width: 16px; height: 16px; }
.link { color: #ff4d8d; font-size: 14px; font-weight: 600; text-decoration: none; }
.link:hover { text-decoration: underline; }

.btn {
  font-family: inherit; font-weight: 700; cursor: pointer; border: none; outline: none;
  border-radius: 14px; padding: 16px; font-size: 15px;
  transition: transform .15s, box-shadow .2s;
  display: flex; align-items: center; justify-content: center;
}
.btn--primary {
  background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%); color: #fff;
  box-shadow: 0 10px 24px rgba(255,77,141,.3);
}
.btn--primary:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(255,77,141,.45); }
.btn--primary:disabled { opacity: .7; cursor: wait; transform: none; }
.spinner {
  width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,.35); border-top-color: #fff;
  border-radius: 50%; animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.divider {
  display: flex; align-items: center; gap: 12px; margin: 14px 0 4px;
  color: #b8a9c9; font-size: 13px;
}
.divider::before, .divider::after {
  content: ''; flex: 1; height: 1px; background: #eee2ec;
}
.socials { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.social {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  background: #fff; border: 2px solid #eee2ec; border-radius: 12px; padding: 12px 8px;
  font-family: inherit; font-weight: 600; font-size: 14px; color: #1a1033;
  cursor: pointer; transition: border-color .2s, transform .15s;
}
.social:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.social:disabled { opacity: 0.5; cursor: wait; transform: none; }

.auth__foot { margin-top: 28px; text-align: center; font-size: 14px; color: #4a3d66; }
.auth__foot a { color: #ff4d8d; font-weight: 700; text-decoration: none; }
.auth__foot a:hover { text-decoration: underline; }

/* ====== RESPONSIVE ====== */
@media (max-width: 960px) {
  .auth {
    grid-template-columns: 1fr;
    background: #7ec8e8;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }
  .auth__scene {
    position: fixed;
    inset: 0;
    height: 100vh;
    height: 100dvh;
  }
  .auth__scene-rose,
  .auth__scene-wash,
  .auth__scene-fill { display: none; }
  .auth__scene-photo {
    inset: 0;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
    background-position: center 18%;
    background-size: cover;
    -webkit-mask-image: none;
    mask-image: none;
  }
  .auth::before,
  .auth::after { content: none; display: none; }

  .auth__hero { display: none; }
  .auth__form-wrap {
    position: relative; z-index: 1;
    margin-left: 0;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 28px 18px 32px;
    align-items: center;
    justify-content: center;
    background: none;
  }
  .auth__form-wrap::before,
  .auth__form-wrap::after { display: none; }
  .auth__form {
    background: rgba(255,255,255,.85);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255,255,255,.7);
    border-radius: 28px;
    padding: 28px 24px 32px;
    box-shadow: 0 30px 60px rgba(160,32,240,.18), 0 8px 24px rgba(26,16,51,.06);
    max-width: 460px;
  }
  .auth__top { margin-bottom: 24px; }
  .brand--mobile { display: flex; }
  .brand--mobile .brand__name {
    background: linear-gradient(90deg, #ff4d8d, #a020f0);
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }
  .backbtn {
    background: rgba(255,255,255,.7);
    box-shadow: 0 4px 12px rgba(26,16,51,.08);
  }
  .auth__title { font-size: 30px; }
}

@media (max-width: 520px) {
  .auth__form-wrap { padding: 22px 16px 28px; }
  .auth__form { padding: 24px 20px 28px; border-radius: 24px; }
  .auth__title { font-size: 26px; line-height: 1.15; }
  .auth__sub { font-size: 14px; margin-bottom: 22px; }
  .field__label { font-size: 12px; }
  .field__box { border-radius: 12px; }
  .field__box input { font-size: 16px; /* prevent iOS zoom on focus */ padding: 13px 0; }
  .btn { padding: 15px; font-size: 15px; border-radius: 12px; }
  .socials { grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
  .social {
    font-size: 0; /* hide label, keep icon only on small phones */
    padding: 12px 8px; border-radius: 10px; gap: 0;
  }
  .social svg { width: 22px; height: 22px; }
  .brand--mobile .brand__name { font-size: 20px; }
  .brand--mobile .brand__icon { width: 32px; height: 32px; }
  .backbtn { width: 36px; height: 36px; font-size: 18px; }
  .check { font-size: 13px; }
  .link { font-size: 13px; }
  .auth__foot { font-size: 13px; margin-top: 24px; }
}

@media (max-width: 360px) {
  .auth__form { padding: 20px 16px 24px; }
  .auth__title { font-size: 23px; }
  .socials { grid-template-columns: 1fr 1fr 1fr; }
}
</style>
